import { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminApi";

interface Ticket {
  TicketID: number;
  SubjectType: string;
  Subject: string;
  Status: string;
  Priority: string;
  CreatedAt: string;
}

interface DriverReport {
  ReportID: number;
  DriverID: number;
  DriverName?: string;
  Category: string;
  Description: string;
  Status: string;
  CreatedAt: string;
}

export default function AdminSupportTickets({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<"tickets" | "driver-reports">("tickets");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [driverReports, setDriverReports] = useState<DriverReport[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "tickets") {
        const res = await adminFetch(`/api/admin/support-tickets`);
        const payload = await res.json();
        if (!res.ok || !payload?.success) throw new Error(payload?.message);
        setTickets(payload.tickets || []);
      } else {
        const res = await adminFetch(`/api/admin/driver-problem-reports`);
        const payload = await res.json();
        if (!res.ok || !payload?.success) throw new Error(payload?.message);
        setDriverReports(payload.reports || []);
      }
    } catch (e) {
      toast.error("Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTicketStatus = async (ticketId: number, status: string) => {
    try {
      const res = await adminFetch(`/api/admin/support-tickets/${ticketId}`, { method: "PUT", body: JSON.stringify({ status }) });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      toast.success("Ticket updated.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to update ticket.");
    }
  };

  const updateReportStatus = async (reportId: number, status: string) => {
    try {
      const res = await adminFetch(`/api/admin/driver-problem-reports/${reportId}`, { method: "PUT", body: JSON.stringify({ status }) });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      toast.success("Report updated.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to update report.");
    }
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-[#0c1428] hover:bg-[#0f1e3d] text-[#a0aec0]"><ArrowLeft size={16} /></button>
          <h1 className="text-white text-lg sm:text-xl font-semibold">Support</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#0a1020] border border-white/[0.08] rounded-lg overflow-hidden">
            <button onClick={() => setTab("tickets")} className={`px-3 py-2 text-sm ${tab === "tickets" ? "bg-[#0f1e3d] text-white" : "text-[#6b7fa3]"}`}>Tickets</button>
            <button onClick={() => setTab("driver-reports")} className={`px-3 py-2 text-sm ${tab === "driver-reports" ? "bg-[#0f1e3d] text-white" : "text-[#6b7fa3]"}`}>Driver reports</button>
          </div>
          <button onClick={() => void load()} className="p-2 bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.07] rounded-lg text-[#a0aec0]"><RefreshCw size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#6b7fa3] text-sm py-14">Loading…</div>
      ) : tab === "tickets" ? (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.TicketID} className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-4 text-sm text-[#e2e8f0] flex justify-between items-center">
              <div>
                <div className="font-medium text-white">{t.Subject}</div>
                <div className="text-[#6b7fa3] text-xs mt-0.5">{t.SubjectType} · {t.Priority} · {new Date(t.CreatedAt).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-[#0a1020] rounded-lg text-xs">{t.Status}</span>
                {t.Status !== "closed" && (
                  <select value={t.Status} onChange={(e) => updateTicketStatus(t.TicketID, e.target.value)} className="bg-[#0a1020] border border-white/[0.08] text-white rounded-lg px-2 py-1.5 text-xs">
                    {["open", "in_progress", "resolved", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>
            </div>
          ))}
          {tickets.length === 0 && <div className="text-center text-[#6b7fa3] text-sm py-14">No support tickets yet.</div>}
        </div>
      ) : (
        <div className="space-y-3">
          {driverReports.map((r) => (
            <div key={r.ReportID} className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-4 text-sm text-[#e2e8f0] flex justify-between items-center">
              <div>
                <div className="font-medium text-white">{r.Category} — {r.DriverName || `Driver #${r.DriverID}`}</div>
                <div className="text-[#a0aec0] text-xs mt-1">{r.Description}</div>
                <div className="text-[#6b7fa3] text-xs mt-1">{new Date(r.CreatedAt).toLocaleString()}</div>
              </div>
              <select value={r.Status} onChange={(e) => updateReportStatus(r.ReportID, e.target.value)} className="bg-[#0a1020] border border-white/[0.08] text-white rounded-lg px-2 py-1.5 text-xs">
                {["open", "in_progress", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
          {driverReports.length === 0 && <div className="text-center text-[#6b7fa3] text-sm py-14">No driver problem reports.</div>}
        </div>
      )}
    </div>
  );
}
